import { OtxProcedureName } from '@store';
import { Observable } from 'rxjs';
import { VddNames } from '../../../../app/+installations/diagnostics-overlay/tabs-content/extended-parameters/diagnostics-constants';

export interface RemoteDiagnosticsSession {
  session: string;
}

export interface RemoteDiagnosticsSeesionGuid {
  data: string;
}

export interface RemoteDiagnosticsSessionClosed {
  success: boolean;
}

export enum RemoteDiagnosticsStatus {
  OPENED = 'OPENED',
  ENDED = 'ENDED',
  RUNNING = 'RUNNING',
  FAILURE = 'FAILURE',
}

export interface RemoteDiagnosticsResponse<T> {
  requestId: string;
  status: RemoteDiagnosticsStatus;
  date: string;
  data: T;
  vdd: string;
  logicalLink: string;
  cycle: number;
  type: string;
  index: number;
}

export interface DiagnosticsStackGenericResponse<T> {
  type: string;
  structure: T;
}

export interface RemoteDiagnosticsSuccessResponseData {
  REQ: string;
  RES: string;
  ecu: string;
  ecuvariant: string;
  requestDate: string;
  request_parameter_name: string;
  request_parameter_value: keyof typeof VddNames;
  securitystatus: string;
  service: string;
  structure: RemoteDiagnosticsVddGenericStructure[];
  type: string;
}

export interface RemoteDiagnosticsFailureResponseData {
  FailureCause: string;
}

export interface RemoteDiagnosticsVddListParameterDetails {
  TI_texttableentry: string;
  interval: string;
  texttableentry: string;
}

export interface RemoteDiagnosticsVddListParameter {
  semantic: string;
  path: string;
  translationid: string;
  dopname: string;
  name: string;
  value: string;
  structure: RemoteDiagnosticsVddListParameterDetails[];
}

export interface RemoteDiagnosticsGetIOControlVddListByComChannel {
  service: string;
  ecu: string;
  state: string;
  type: string;
  structure: RemoteDiagnosticsVddListParameter[];
}

export interface RemoteDiagnosticsGetIOControlVddListByComChannelGeneric {
  type: string;
  structure: RemoteDiagnosticsGetIOControlVddListByComChannel[];
}

export interface RemoteDiagnosticsGetVddListByComChannelGeneric {
  service: string;
  ecu: string;
  type: string;
  structure: RemoteDiagnosticsGetVddListByComChannel[];
}

export interface RemoteDiagnosticsGetVddListByComChannel {
  semantic: string;
  path: string;
  unit?: string;
  translationid: string;
  dopname: string;
  name: string;
  value: string;
  structure: RemoteDiagnosticsVddItemByComChannel[];
}

export interface RemoteDiagnosticsVddItemByComChannel {
  TI_texttableentry: string;
  isRead: string; // this is actually a boolean which comes as string
  interval: string; // number
  texttableentry: string;
  isWrite: string; // boolean
}

export interface RemoteDiagnosticsVciRegistration {
  connectionId: string; // this is actually a number which comes as string
}

export interface RemoteDiagnosticsVciAvailable {
  name: string;
  resources: string[];
  vciType: string;
  vendorName: string;
  description: string;
  hardwareName: string;
  hardwareSerialNumber: string;
  hardwareVersion: string;
  firmwareName: string;
  firmwareVersion: string;
  additionalAttributes?: RemoteDiagnosticsVciAvailableAttributes;
}

export interface RemoteDiagnosticsVciAvailableAttributes {
  [key: string]: any;
  vin: string;
  webSocketURL?: string; // removed as of version diag stack 405, kept to avoid need for BE release sync
  ipAddress?: string;
}

export interface Sessions {
  max: number;
  current: number;
}

export interface Slots {
  max: number;
  current: number;
}

export interface DiagStackNode {
  hostAndPath: string;
  sessions?: Sessions;
  slots?: Slots;
}

export interface DiagStackNodeError {
  code: number;
  reason: string;
  details: string;
}

export interface DiagStackStructure {
  semantic: string;
  path: string;
  unit?: string;
  translationid: string;
  dopname: string;
  name: string;
  value?: string;
  valuetranslationid?: string;
  structure?: DiagStackStructure[];
}

export interface MainDiagStackStructure {
  severity: string;
  RES: string;
  request_parameter_name: string;
  service: string;
  ecu: string;
  requestDate: string;
  ecuvariant: string;
  request_parameter_value: string;
  securitystatus: string;
  type: string;
  REQ: string;
  structure?: DiagStackStructure[];
}

export interface DiagStackGenericResponse {
  type: string;
  structure: MainDiagStackStructure[];
}

export interface DiagStackOTXSequenceParameter {
  name: string;
  type: string;
  behavior: string;
}

export interface DiagStackAvailableOTXSequence {
  name: OtxProcedureName;
  nameSpace: string;
  parameters: DiagStackOTXSequenceParameter[];
}

// Use Cases

export interface UseCaseGroup {
  TI_texttableentry: string;
  interval: string;
  texttableentry: string;
}

export interface UseCaseTyps {
  unit: string;
  usecasetype: UseCaseTypesEnum;
  structure: UseCaseGroup[];
}

export interface UseCases {
  type: string;
  structure: UseCaseTyps[];
}

// Only the last 5 types are returned by the backend
// ServiceLayer is defined here in order not to create separate enum
export enum UseCaseTypesEnum {
  ServiceLayer = 'ServiceLayer',
  ConfigurationOverview = 'ConfigurationOverview',
  DiagnosisOverview = 'DiagnosisOverview',
  InspectionCertificate = 'InspectionCertificate',
  InspectionDashboard = 'InspectionDashboard',
  HmiSimulator = 'HmiSimulator',
}

export interface UseCaseVddList {
  usecaseName: string;
  usecaseType: string;
  structure: UseCaseVdd[];
}

export interface UseCaseVdd {
  vdd: string;
}

export interface RemoteDiagnosticsVddRootMetadataResponse {
  request_parameter_name: string;
  service: string;
  ecu: string;
  request_parameter_value: keyof typeof VddNames;
  type: string;
  structure: RemoteDiagnosticsVddGenericStructure[];
}

export interface RemoteDiagnosticsVddRootStructureResponse {
  ecu: string;
  request_parameter_name: string;
  request_parameter_value: keyof typeof VddNames;
  service: string;
  structure: RemoteDiagnosticsVddGenericStructure[];
  type: string;
}

export interface RemoteDiagnosticsVddGenericStructure {
  semantic?: string;
  longname?: string;
  byteposition?: string;
  dopname?: string;
  precision?: string;
  byteLength?: string;
  type?: string;
  shortname?: string;
  constraints?: string;
  path?: string;
  unit?: string;
  translationid?: string;
  bitposition?: string;
  name?: string;
  interval?: string;
  value?: string;
  TI_texttableentry?: string;
  texttableentry?: string;
  structure?: RemoteDiagnosticsVddGenericStructure[];
  skip?: boolean; // Used for mac address/ip address skip
  isSensor?: boolean; // Artificial, to avoid using isSensor method all the time
}

export interface SetNewValueObject {
  path: string;
  newValue: string;
}

export interface RemoteDiagnosticsCloudApiContract {
  vddList(
    hostAndPath: string,
    xAuthToken: string,
    logicalLink: string,
    vciName?: string,
    resource?: string
  ): Observable<RemoteDiagnosticsGetVddListByComChannelGeneric>;

  openDiagStackConnection(
    gatewaySerial: string,
    reason: string
  ): Observable<RemoteDiagnosticsSeesionGuid>;

  closeDiagStackConnection(
    gatewaySerial: string,
    reason: string
  ): Observable<RemoteDiagnosticsSessionClosed>;
}
